import keycloak from './keycloak';

/**
 * API auth token from environment variable (.env)
 * When set, this token is used as the Bearer token for all API calls,
 * bypassing Keycloak authentication.
 */
const API_AUTH_TOKEN = process.env.REACT_APP_API_AUTH_TOKEN;

/**
 * Helper to create authenticated headers for API calls
 * If REACT_APP_API_AUTH_TOKEN is set in .env, it is used as the Bearer token.
 * Otherwise falls back to Keycloak token.
 */
export const getAuthHeaders = async () => {
  // If env token is set, use it directly — skip Keycloak entirely
  if (API_AUTH_TOKEN) {
    return {
      'Authorization': `Bearer ${API_AUTH_TOKEN}`,
      'Content-Type': 'application/json',
    };
  }

  if (!keycloak.authenticated) {
    const storedToken = localStorage.getItem('keycloak_token');
    if (storedToken) {
      return {
        'Authorization': `Bearer ${storedToken}`,
        'Content-Type': 'application/json',
      };
    }
    throw new Error('User is not authenticated.');
  }

  // Check if token needs refresh (refresh if it expires in the next 30 seconds)
  if (keycloak.tokenParsed && keycloak.tokenParsed.exp) {
    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = keycloak.tokenParsed.exp - now;
    
    if (timeUntilExpiry < 30) {
      try {
        await keycloak.updateToken(70);
      } catch (error) {
        console.error('[Keycloak API] Token refresh failed:', error);
        keycloak.login();
        throw new Error('Authentication expired. Please log in again.');
      }
    }
  }

  if (!keycloak.token) {
    const storedToken = localStorage.getItem('keycloak_token');
    if (storedToken) {
      return {
        'Authorization': `Bearer ${storedToken}`,
        'Content-Type': 'application/json',
      };
    }
    throw new Error('Token is missing. Please log in again.');
  }

  return {
    'Authorization': `Bearer ${keycloak.token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper
 * Automatically includes Bearer token and handles token refresh
 */
export const authFetch = async (url, options = {}) => {
  try {
    const headers = await getAuthHeaders();
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });
    
    if (response.status === 401) {
      // Token is invalid, try to refresh
      try {
        await keycloak.updateToken(70);
        const newHeaders = await getAuthHeaders();
        
        const retryResponse = await fetch(url, {
          ...options,
          headers: {
            ...newHeaders,
            ...options.headers,
          },
        });
        
        return retryResponse;
      } catch (refreshError) {
        console.error('[Keycloak API] Token refresh failed:', refreshError);
        keycloak.login();
        throw new Error('Authentication failed. Please log in again.');
      }
    }
    
    return response;
  } catch (error) {
    console.error('[Keycloak API] authFetch error:', error);
    throw error;
  }
};

/**
 * Get current user info from Keycloak token
 */
export const getCurrentUser = () => {
  if (!keycloak.authenticated || !keycloak.tokenParsed) {
    return null;
  }

  return {
    username: keycloak.tokenParsed.preferred_username || 'unknown',
    email: keycloak.tokenParsed.email || '',
    sub: keycloak.tokenParsed.sub || '',
    name: keycloak.tokenParsed.name || '',
    roles: keycloak.tokenParsed.realm_access?.roles || [],
  };
};
